import React, { useEffect, useState, useRef } from "react";
import ChatAICliente from "./ai/ChatAICliente";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  setDoc,
  updateDoc
} from "firebase/firestore";
import "./MenuView.css";
import AICliente from "./ai/AICliente";
import { defaultUIStrings } from "./uiStrings";
import { Icons } from "./icons";
// DOPO gli import 👇
const getCatId = (name) =>
  String(name).toLowerCase().replace(/\s+/g, "-");
const getColor = (stato) => {
  if (stato === "servito") return "#9e9e9e"; // 🔥 AGGIUNGI
  if (stato === "pronto") return "#4caf50";
  if (stato === "in preparazione") return "#ff9800";
  if (stato === "in attesa") return "#ffc107";
  return "#999";
};

/**
 * Versione avanzata: calcola similarità con pesi diversi per parole chiave
 */
const calcolaSimilaritaPiattiAvanzata = (dish1, dish2) => {
  if (dish1.id === dish2.id) return 0;
  
  const getIngredienti = (dish) => {
    if (Array.isArray(dish.ingredienti)) {
      return dish.ingredienti.map(i => i.toLowerCase().trim());
    }
    if (typeof dish.ingredienti === "string") {
      return dish.ingredienti.split(",").map(i => i.toLowerCase().trim()).filter(Boolean);
    }
    return [];
  };
  
  const ing1 = getIngredienti(dish1);
  const ing2 = getIngredienti(dish2);
  
  // Parole chiave per gruppi di ingredienti (pesi maggiori se combaciano)
  const gruppiIngredienti = {
    "pesce": ["pesce", "salmone", "tonno", "orata", "branzino", "mare", "gamberi", "calamari"],
    "carne": ["carne", "manzo", "maiale", "pollo", "agnello", "bistecca", "tagliata"],
    "formaggio": ["formaggio", "mozzarella", "parmigiano", "pecorino", "gorgonzola", "ricotta"],
    "verdure": ["verdure", "spinaci", "zucchine", "melanzane", "pomodoro", "insalata"],
    "pasta": ["pasta", "spaghetti", "tortellini", "lasagne", "gnocchi"],
    "vino": ["vino", "rosso", "bianco", "rosato", "champagne"],
    "dolce": ["dolce", "cioccolato", "tiramisù", "panna", "crema"]
  };
  
  // Calcola similarità basata su gruppi
  let punteggioGruppi = 0;
  let gruppiTrovati1 = new Set();
  let gruppiTrovati2 = new Set();
  
  for (const [gruppo, parole] of Object.entries(gruppiIngredienti)) {
    const hasGruppo1 = ing1.some(ing => parole.some(p => ing.includes(p)));
    const hasGruppo2 = ing2.some(ing => parole.some(p => ing.includes(p)));
    
    if (hasGruppo1) gruppiTrovati1.add(gruppo);
    if (hasGruppo2) gruppiTrovati2.add(gruppo);
    
    if (hasGruppo1 && hasGruppo2) {
      punteggioGruppi += 0.3; // Bonus per stesso gruppo
    }
  }
  
  // Similarità Jaccard base
  const set1 = new Set(ing1);
  const set2 = new Set(ing2);
  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);
  const jaccard = union.size === 0 ? 0 : intersection.size / union.size;
  
  // Similarità basata sulla categoria
  const cat1 = (dish1.category || dish1.categoria || "").toLowerCase();
  const cat2 = (dish2.category || dish2.categoria || "").toLowerCase();
  const stessaCategoria = cat1 === cat2 ? 0.2 : 0;
  
  // Punteggio finale (max 1)
  let punteggio = jaccard * 0.5 + punteggioGruppi * 0.3 + stessaCategoria * 0.2;
  
  return Math.min(punteggio, 1);
};
// ===========================
// FUNZIONE CALCOLO SIMILARITÀ PIATTI
// ===========================

/**
 * Calcola la similarità tra due piatti basata sugli ingredienti
 * @param {Object} dish1 - Primo piatto
 * @param {Object} dish2 - Secondo piatto
 * @returns {number} Punteggio di similarità (0-1)
 */
const calcolaSimilaritaPiatti = (dish1, dish2) => {
  // Se sono lo stesso piatto, similarità massima ma non la useremo
  if (dish1.id === dish2.id) return 0;
  
  // Ottieni ingredienti normalizzati
  const getIngredienti = (dish) => {
    if (Array.isArray(dish.ingredienti)) {
      return dish.ingredienti.map(i => i.toLowerCase().trim());
    }
    if (typeof dish.ingredienti === "string") {
      return dish.ingredienti.split(",").map(i => i.toLowerCase().trim()).filter(Boolean);
    }
    return [];
  };
  
  const ing1 = new Set(getIngredienti(dish1));
  const ing2 = new Set(getIngredienti(dish2));
  
  // Se entrambi non hanno ingredienti, usa la categoria come fallback
  if (ing1.size === 0 && ing2.size === 0) {
    const cat1 = (dish1.category || dish1.categoria || "").toLowerCase();
    const cat2 = (dish2.category || dish2.categoria || "").toLowerCase();
    return cat1 === cat2 ? 0.5 : 0.2;
  }
  
  // Se uno dei due non ha ingredienti, similarità bassa
  if (ing1.size === 0 || ing2.size === 0) return 0.1;
  
  // Calcola intersezione e unione
  const intersection = new Set([...ing1].filter(x => ing2.has(x)));
  const union = new Set([...ing1, ...ing2]);
  
  // Jaccard similarity
  return intersection.size / union.size;
};

const trovaPiattiSimili = (dish, allDishes, limit = 5) => {
  return allDishes
    .filter(d => d.id !== dish.id && d.visibile !== false)
    .map(d => ({
      ...d,
      score: calcolaSimilaritaPiattiAvanzata(dish, d)
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};


const resolveDishText = (dish, lingua) => {
  const getItalianName = () => {
    if (typeof dish.nome === "string") return dish.nome;
    if (dish.nome?.it) return dish.nome.it;
    return "";
  };

  const getItalianDescription = () => {
    if (typeof dish.descrizione === "string") return dish.descrizione;
    if (dish.descrizione?.it) return dish.descrizione.it;
    return "";
  };

  const getItalianIngredients = () => {
    if (Array.isArray(dish.ingredienti)) return dish.ingredienti;
    if (typeof dish.ingredienti === "string")
      return dish.ingredienti.split(",").map(i => i.trim()).filter(Boolean);
    return [];
  };

  const getItalianAllergens = () => {
    if (Array.isArray(dish.allergeni)) return dish.allergeni;
    if (typeof dish.allergeni === "string")
      return dish.allergeni.split(",").map(a => a.trim()).filter(Boolean);
    return [];
  };

  if (lingua === "it") {
    return {
      name: getItalianName(),
      description: getItalianDescription(),
      ingredients: getItalianIngredients(),
      allergens: getItalianAllergens()
    };
  }

  const name =
    dish.translations?.[lingua]?.name ||
    dish.translations?.[lingua]?.nome ||
    getItalianName();

  const description =
    dish.translations?.[lingua]?.description ||
    dish.translations?.[lingua]?.descrizione ||
    getItalianDescription();

  const ingredients =
    Array.isArray(dish.translations?.[lingua]?.ingredients) &&
    dish.translations[lingua].ingredients.length > 0
      ? dish.translations[lingua].ingredients
      : getItalianIngredients();

  const allergens =
    Array.isArray(dish.translations?.[lingua]?.allergens) &&
    dish.translations[lingua].allergens.length > 0
      ? dish.translations[lingua].allergens
      : getItalianAllergens();

  return { name, description, ingredients, allergens };
};

const resolveAllergensForView = (dish, lingua) => {
  const { allergens } = resolveDishText(dish, lingua);

  if (
    lingua !== "it" &&
    Array.isArray(dish.translations?.[lingua]?.allergens) &&
    dish.translations[lingua].allergens.length > 0
  ) {
    return allergens;
  }

  return Array.isArray(allergens)
    ? allergens.map(a => translateAllergen(a, lingua))
    : [];
};


const ALLERGENI_I18N = {
  it: {
    "Latte": "Latte",
    "Uova": "Uova",
    "Pesce": "Pesce",
    "Crostacei": "Crostacei",
    "Cereali contenenti glutine": "Gluten",
    "Soia": "Soia",
    "Frutta a guscio": "Frutta a guscio",
    "Sedano": "Sedano",
    "Senape": "Senape",
    "Sesamo": "Sesamo",
    "Solfiti": "Solfiti",
    "Lupini": "Lupini",
    "Molluschi": "Molluschi"
  },
  en: {
    "Latte": "Milk",
    "Uova": "Eggs",
    "Pesce": "Fish",
    "Crostacei": "Crustaceans",
    "Cereali contenenti glutine": "Gluten",
    "Soia": "Soy",
    "Frutta a guscio": "Nuts",
    "Sedano": "Celery",
    "Senape": "Mustard",
    "Sesamo": "Sesame",
    "Solfiti": "Sulphites",
    "Lupini": "Lupin",
    "Molluschi": "Molluscs"
  }
};

const translateAllergen = (label, lang) =>
  ALLERGENI_I18N[lang]?.[label] || label;

// ===========================
// GROUP ITEMS BY STATO (PER ORDINI INVIATI)
// ===========================
function groupItemsByStato(items = []) {
  const grouped = {};

  items.forEach((item) => {
    const stato = (item.stato || "inviato").toLowerCase();
    const nome = item.name || item.nome || "Piatto";

    const key = `${nome}_${stato}_${item.custom || ""}`;

    if (!grouped[key]) {
      grouped[key] = {
        ...item,
        name: nome,
        stato,
        quantity: item.quantity || 1
      };
    } else {
      grouped[key].quantity += item.quantity || 1;
    }
  });

  return Object.values(grouped);
}


// ...existing code...

const SuggestionPopup = React.memo(({
  suggestionPopup,
  selectedSuggestions,
  setSuggestionPopup,
  setSelectedSuggestions,
  fetchAISuggestions,
  addDishToCart,
  TT
}) => {
  if (!suggestionPopup.open || !suggestionPopup.dish) return null;

  const dishName = typeof suggestionPopup.dish.nome === "string"
    ? suggestionPopup.dish.nome
    : suggestionPopup.dish.nome?.it || "";

  // Stato 1: Chiedi se vuoi suggerimenti
  if ((!suggestionPopup.suggestions || suggestionPopup.suggestions.length === 0) && !suggestionPopup.loading && !suggestionPopup.asked) {
    return (
      <div
        className="suggestion-popup-overlay"
        onClick={() => {
          setSuggestionPopup({ open: false, dish: null, suggestions: [], loading: false, quantity: 1, custom: "", asked: false });
          setSelectedSuggestions(new Set());
        }}
      >
        <div className="suggestion-popup-content" onClick={(e) => e.stopPropagation()}>
          <button
            className="suggestion-popup-close"
            onClick={() => {
              setSuggestionPopup({ open: false, dish: null, suggestions: [], loading: false, quantity: 1, custom: "", asked: false });
              setSelectedSuggestions(new Set());
            }}
          >✕</button>
          <h3>{TT.suggestionPopupTitle || "Ti consiglio qualcosa?"}</h3>
          <p>{TT.suggestionPopupText || "Vuoi che ti suggerisca degli abbinamenti per"} <strong>{dishName}</strong>?</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
            <button
              className="suggestion-btn-yes"
              onClick={() => {
                setSuggestionPopup((prev) => ({ ...prev, asked: true }));
                fetchAISuggestions(suggestionPopup.dish);
              }}
            >{TT.suggestionYes || "Sì, suggeriscimi"}</button>
            <button
              className="suggestion-btn-no"
              onClick={() => addDishToCart(suggestionPopup.dish, suggestionPopup.quantity, suggestionPopup.custom, new Set())}
            >{TT.suggestionNo || "No, solo questo"}</button>
          </div>
        </div>
      </div>
    );
  }

  // Stato 2: Caricamento
  if (suggestionPopup.loading) {
    return (
      <div
        className="suggestion-popup-overlay"
        onClick={() => {
          setSuggestionPopup({ open: false, dish: null, suggestions: [], loading: false, quantity: 1, custom: "", asked: false });
          setSelectedSuggestions(new Set());
        }}
      >
        <div className="suggestion-popup-content" onClick={(e) => e.stopPropagation()}>
          <button
            className="suggestion-popup-close"
            onClick={() => {
              setSuggestionPopup({ open: false, dish: null, suggestions: [], loading: false, quantity: 1, custom: "", asked: false });
              setSelectedSuggestions(new Set());
            }}
          >✕</button>
          <h3>{TT.suggestionPopupTitle}</h3>
          <p style={{ marginTop: "1rem", fontStyle: "italic", textAlign: "center" }}>
            ⏳ {TT.suggestionLoading || "Sto cercando gli abbinamenti migliori..."}
          </p>
        </div>
      </div>
    );
  }

// Stato 3: Mostra suggerimenti
if (suggestionPopup.suggestions.length > 0) {
  return (
    <div
      className="suggestion-popup-overlay"
      onClick={() => {
        setSuggestionPopup({
          open: false,
          dish: null,
          suggestions: [],
          loading: false,
          quantity: 1,
          custom: "",
          asked: false
        });
        setSelectedSuggestions(new Set());
      }}
    >
      <div
        className="suggestion-popup-content suggestion-popup-results"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="suggestion-popup-close"
          onClick={() => {
            setSuggestionPopup({
              open: false,
              dish: null,
              suggestions: [],
              loading: false,
              quantity: 1,
              custom: "",
              asked: false
            });
            setSelectedSuggestions(new Set());
          }}
        >
          ✕
        </button>

        <div className="suggestion-results-header">
          <span className="suggestion-ai-badge">AI</span>

          <div>
            <h3>Abbinamenti suggeriti</h3>
            <p>
              Per <strong>{dishName}</strong>
            </p>
          </div>
        </div>

        <div className="suggestion-results-list">
          {suggestionPopup.suggestions
            .sort((a, b) => (b.score || 0) - (a.score || 0))
            .map((sugg) => {
              const suggName =
                typeof sugg.nome === "string"
                  ? sugg.nome
                  : sugg.nome?.it || "";

              const isSelected = selectedSuggestions.has(sugg.id);

              return (
                <div
                  key={sugg.id}
                  className={`suggestion-dish-card ${
                    isSelected ? "selected" : ""
                  }`}
                >
                  <div className="suggestion-dish-image-wrapper">
                    {sugg.image ? (
                      <img
                        src={sugg.image}
                        alt={suggName}
                        className="suggestion-dish-image"
                      />
                    ) : (
                      <div className="suggestion-dish-no-image">
                        🍽️
                      </div>
                    )}
                  </div>

                  <div className="suggestion-dish-info">
                    <div className="suggestion-dish-name">
                      {suggName}
                    </div>

                    {sugg.price != null && (
                      <div className="suggestion-dish-price">
                        € {Number(sugg.price).toFixed(2)}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={`suggestion-add-btn ${
                      isSelected ? "selected" : ""
                    }`}
                    aria-label={
                      isSelected
                        ? `Rimuovi ${suggName}`
                        : `Aggiungi ${suggName}`
                    }
                    onClick={() => {
                      const newSelected = new Set(
                        selectedSuggestions
                      );

                      if (isSelected) {
                        newSelected.delete(sugg.id);
                      } else {
                        newSelected.add(sugg.id);
                      }

                      setSelectedSuggestions(newSelected);
                    }}
                  >
                    {isSelected ? "✓" : "+"}
                  </button>
                </div>
              );
            })}
        </div>

<div className="suggestion-results-actions">

  {selectedSuggestions.size > 0 && (
    <button
      className="suggestion-btn-yes"
      onClick={() =>
        addDishToCart(
          suggestionPopup.dish,
          suggestionPopup.quantity,
          suggestionPopup.custom,
          selectedSuggestions
        )
      }
    >
      ✓ Aggiungi con abbinamento
    </button>
  )}

  <button
    className="suggestion-btn-no"
    onClick={() =>
      addDishToCart(
        suggestionPopup.dish,
        suggestionPopup.quantity,
        suggestionPopup.custom,
        new Set()
      )
    }
  >
    Solo il piatto
  </button>

</div>
      </div>
    </div>
  );
}

  return null;
});

// ===========================
// COMPONENTE PRINCIPALE
// ===========================

function MenuView() {
    // Debug mode per logging suggerimenti
    const [debugMode, setDebugMode] = useState(false);
  const { localeTag, tableId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const tokenUrl = urlParams.get("token");

  const normalizedLocaleTag = localeTag
    .toLowerCase()
    .replace(/[^a-z0-9_-]/gi, "");

  const ordinePath = `ordiniTemp/${normalizedLocaleTag}___${tableId}/items`;
  const ordiniInviatiPath = `ordiniTemp/${normalizedLocaleTag}___${tableId}/sent`;

  // ===========================
  // STATI PRINCIPALI
  // ===========================
  const [aiOpen, setAiOpen] = useState(false);
const [chatOpen, setChatOpen] = useState(false);
const [showMenuIntro, setShowMenuIntro] = useState(false);
const [showChatCallout, setShowChatCallout] = useState(false);
  const [suggestionPopup, setSuggestionPopup] = useState({
    open: false,
    dish: null,
    suggestions: [],
    loading: false,
    quantity: 1,
    custom: "",
    asked: false
  });
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());

  const [infoLocale, setInfoLocale] = useState({});
  const isDemo = infoLocale?.demo === true;
  const tornaAllaDemoDashboard = () => {
  navigate(`/dashboard-gestore?locale=${normalizedLocaleTag}`);
};
const chiudiMenuIntro = () => {
  localStorage.setItem(
    `qrmenu_menu_intro_v2_${normalizedLocaleTag}`,
    "true"
  );

  setShowMenuIntro(false);
};
const apriChatQira = () => {
  setChatOpen(true);
  setShowChatCallout(false);

  sessionStorage.setItem(
    `qrmenu_qira_chat_callout_v1_${normalizedLocaleTag}`,
    "true"
  );
};

const chiudiChatCallout = (e) => {
  e.stopPropagation();

  setShowChatCallout(false);

  sessionStorage.setItem(
    `qrmenu_qira_chat_callout_v1_${normalizedLocaleTag}`,
    "true"
  );
};
  const [localeBloccato, setLocaleBloccato] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [ordineCondiviso, setOrdineCondiviso] = useState([]);

  const [note, setNote] = useState("");
  const [inputValues, setInputValues] = useState({});
  const [clienteId, setClienteId] = useState("");

  const [loading, setLoading] = useState(true);
  
  // Inizializza lingua con priorità: localStorage → browser → "it"
  const [lingua, setLingua] = useState(() => {
  return localStorage.getItem("menu_lang") || "it";
});

  const [showRiepilogo, setShowRiepilogo] = useState(false);
  const [showOrdiniInviati, setShowOrdiniInviati] = useState(false);
  const [chiamataAttiva, setChiamataAttiva] = useState(false);
  const [ordiniInviati, setOrdiniInviati] = useState([]);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  // Token
  const [tokenScaduto, setTokenScaduto] = useState(false);
  const [verificaTokenLoading, setVerificaTokenLoading] = useState(true);

  // AI

  // Popup immagine premium
  const [popupDish, setPopupDish] = useState(null);

  // Sponsor / Promo / Banners
  const [banners, setBanners] = useState([]);
  const [bannersPromozionali, setBannersPromozionali] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [currentBannerPromoIndex, setCurrentBannerPromoIndex] = useState(0);

  const [currentSponsor, setCurrentSponsor] = useState(null);
  const [showSponsor, setShowSponsor] = useState(false);

  const [currentPromozione, setCurrentPromozione] = useState(null);
  const [showPromozione, setShowPromozione] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "info"
  });

  // Modalità dark/light auto
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Categoria selezionata dalla navbar
  const [activeCategory, setActiveCategory] = useState(null);

  // Ref per sidebar auto-scroll
  const sidebarRef = useRef(null);

  const accessTrackedRef = useRef(false);

  // ===========================
  // TRADUZIONI
  // ===========================

  const [uiTranslations, setUiTranslations] = useState({});
  const [aiPreferences, setAiPreferences] = useState(null);

  const t = {
    it: defaultUIStrings,
    en: {
      loading: "Loading...",
      allergens: "Allergens",
      placeholder: "E.g. without onion",
      add: "Add",
      callService: "Call service",
      orderSummary: "Order summary",
      sendOrder: "Send order",
      close: "Close",
      notesPlaceholder: "Extra notes...",
      sentOrders: "Sent orders",
      aiAssistantTitle: "AI Assistant",
      aiPostOrderTitle: "AI Chef suggestion",
      aiPostOrderClose: "Got it",
      itemAdded: "Item added to cart",
      orderSent: "Order sent to the kitchen"
    }
  };

  const TT = uiTranslations[lingua] || t[lingua] || t.it;

  // ===============================
  // Microcopy fallback (UI safety)
  // ===============================
  const microcopyFallback = {
    it: {
      recommendedBadge: "Consigliato",
      notesHintPlaceholder: "Nota (es. senza cipolla)",
      addToOrderA11y: "Aggiungi al tuo ordine",
      removedFromOrder: "Rimosso dal tuo ordine",
      addError: "Non sono riuscito ad aggiungerlo",
      noPairingFound: "Nessun abbinamento trovato",
      pairingLoadError: "Errore nel caricamento dei consigli",
      suggestionPopupTitle: "Vuoi un consiglio veloce?",
      suggestionPopupText: "Posso suggerirti un abbinamento perfetto per",
      suggestionYes: "Sì, consigliami",
      suggestionNo: "No grazie, continua",
      suggestionLoading: "Sto pensando agli abbinamenti…",
      addSelectionCta: "✅ Aggiungi con abbinamenti",
      onlyDishCta: "➕ Solo questo piatto",
      menuHelperLine: "💡 Se vuoi, posso aiutarti a scegliere i piatti migliori per te."
    },
    en: {
      recommendedBadge: "Recommended",
      notesHintPlaceholder: "Note (e.g. no onion)",
      addToOrderA11y: "Add to your order",
      removedFromOrder: "Removed from your order",
      addError: "Couldn’t add it",
      noPairingFound: "No pairing found",
      pairingLoadError: "Error loading suggestions",
      suggestionPopupTitle: "Want a quick tip?",
      suggestionPopupText: "I can suggest a perfect pairing for",
      suggestionYes: "Yes, suggest",
      suggestionNo: "No thanks, continue",
      suggestionLoading: "Thinking of pairings…",
      addSelectionCta: "✅ Add with pairings",
      onlyDishCta: "➕ Only this dish",
      menuHelperLine: "💡 If you want, I can help you choose."
    }
  };

  // Helper per recuperare testo UI con fallback sicuro
  const TXT = (key) =>
    TT?.[key] ||
    microcopyFallback?.[lingua]?.[key] ||
    microcopyFallback.it[key] ||
    key;

  // ===========================
  // UTIL SNACKBAR
  // ===========================

  const showSnack = (message, type = "info", duration = 3000) => {
    setSnackbar({ visible: true, message, type });
    setTimeout(() => {
      setSnackbar({ visible: false, message: "", type: "info" });
    }, duration);
  };



  // ===========================
  // TOTALE PIATTI CARRELLO
  // ===========================

  const totalePiatti = ordineCondiviso.reduce(
    (acc, item) => acc + (item.quantity || 0),
    0
  );

  // ===========================
  // TOTALE PER SINGOLO PIATTO
  // ===========================

  const getTotalQuantityForDish = (dishId) => {
    return ordineCondiviso
      .filter((item) => item.dishId === dishId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  // ===========================
  // LINGUE DISPONIBILI
  // ===========================

  const availableLangs = React.useMemo(() => {
    const langSet = new Set(["it"]); // sempre "it" come fallback
    
    dishes.forEach((dish) => {
      // Legge da translations
      if (dish.translations && typeof dish.translations === "object") {
        Object.keys(dish.translations).forEach((lang) => {
          langSet.add(lang);
        });
      }
      
      // Legge da nome
      if (dish.nome && typeof dish.nome === "object") {
        Object.keys(dish.nome).forEach((lang) => {
          langSet.add(lang);
        });
      }
      
      // Legge da descrizione
      if (dish.descrizione && typeof dish.descrizione === "object") {
        Object.keys(dish.descrizione).forEach((lang) => {
          langSet.add(lang);
        });
      }
    });

    const langs = Array.from(langSet);
    // "it" per primo, poi altre lingue in ordine alfabetico
    return ["it", ...langs.filter((l) => l !== "it").sort()];
  }, [dishes]);

  // Mappa delle bandiere per le lingue (codici ISO 639-1)
  const langFlags = {
    it: "🇮🇹",
    en: "🇬🇧",
    fr: "🇫🇷",
    de: "🇩🇪",
    es: "🇪🇸",
    pt: "🇵🇹",
  };

    const langLabels = {
      it: "Italiano",
      en: "English",
      fr: "Français",
      de: "Deutsch",
      es: "Español",
      pt: "Português",
    };

    useEffect(() => {
  localStorage.setItem("menu_lang", lingua);
}, [lingua]);

// ===========================
// INTRO QIRA E GIOCHI
// ===========================
useEffect(() => {
  if (verificaTokenLoading) return;
  if (tokenScaduto) return;
  if (loading) return;
  if (!normalizedLocaleTag) return;
  if (infoLocale?.nascondiAI === true) return;

  const storageKey =
    `qrmenu_menu_intro_v2_${normalizedLocaleTag}`;

  const introGiaVista =
    localStorage.getItem(storageKey) === "true";

  if (introGiaVista) return;

  const timer = setTimeout(() => {
    setShowMenuIntro(true);
  }, 1400);

  return () => clearTimeout(timer);
}, [
  verificaTokenLoading,
  tokenScaduto,
  loading,
  normalizedLocaleTag,
  infoLocale?.nascondiAI
]);

// ===========================
// CALLOUT CHAT QIRA
// ===========================
useEffect(() => {
  if (verificaTokenLoading) return;
  if (tokenScaduto) return;
  if (loading) return;
  if (!normalizedLocaleTag) return;
  if (infoLocale?.nascondiAI === true) return;
  if (!infoLocale?.consentOrdine) return;
  if (showMenuIntro) return;

  const introKey =
    `qrmenu_menu_intro_v2_${normalizedLocaleTag}`;

  const calloutKey =
    `qrmenu_qira_chat_callout_v1_${normalizedLocaleTag}`;

  const introGiaVista =
    localStorage.getItem(introKey) === "true";

  const calloutGiaVista =
    sessionStorage.getItem(calloutKey) === "true";

  if (!introGiaVista || calloutGiaVista) return;

  const timer = setTimeout(() => {
    setShowChatCallout(true);
  }, 2500);

  return () => clearTimeout(timer);
}, [
  verificaTokenLoading,
  tokenScaduto,
  loading,
  normalizedLocaleTag,
  infoLocale?.nascondiAI,
  infoLocale?.consentOrdine,
  showMenuIntro
]);

// ===========================
// CALCOLO ALTEZZA REALE BANNER
// ===========================
useEffect(() => {

  const updateBannerHeight = () => {

    const banner =
      document.querySelector(".mv-banner-wrapper");

    const height = banner
      ? banner.offsetHeight
      : 0;

    document.documentElement.style.setProperty(
      "--banner-height",
      `${height}px`
    );
  };

  // primo calcolo
  updateBannerHeight();

  // aggiorna quando cambia dimensione finestra
  window.addEventListener(
    "resize",
    updateBannerHeight
  );

  // osserva cambiamenti immagine/banner
  const banner =
    document.querySelector(".mv-banner-wrapper");

  let observer;

  if (banner) {

    observer = new ResizeObserver(() => {
      updateBannerHeight();
    });

    observer.observe(banner);
  }

  return () => {

    window.removeEventListener(
      "resize",
      updateBannerHeight
    );

    if (observer) {
      observer.disconnect();
    }
  };

}, [bannersPromozionali, currentBannerPromoIndex]);

  // ===========================
  // UseEffect + Firebase
  // ===========================


  // Isola scroll touch della sidebar per evitare propagazione al body
  useEffect(() => {
    const sidebar = document.querySelector(".mv-sidebar-inner");
    if (!sidebar) return;

    const stopTouch = (e) => {
      e.stopPropagation();
    };

    sidebar.addEventListener("touchstart", stopTouch, { passive: true });
    sidebar.addEventListener("touchmove", stopTouch, { passive: true });

    return () => {
      sidebar.removeEventListener("touchstart", stopTouch);
      sidebar.removeEventListener("touchmove", stopTouch);
    };
  }, []);

  // Dark / Light mode automatica
  useEffect(() => {
    try {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const update = (e) => setIsDarkMode(e.matches);
      setIsDarkMode(mq.matches);

      if (mq.addEventListener) mq.addEventListener("change", update);
      else mq.addListener(update);

      return () => {
        if (mq.removeEventListener) mq.removeEventListener("change", update);
        else mq.removeListener(update);
      };
    } catch {
      setIsDarkMode(false);
    }
  }, []);

  // Genera ID cliente unico
  useEffect(() => {
    const generateUUID = () => {
      if (crypto.randomUUID) return crypto.randomUUID();
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    const id = localStorage.getItem("clienteId") || generateUUID();
    localStorage.setItem("clienteId", id);
    setClienteId(id);
  }, []);

  // ===========================
// VERIFICA TOKEN REALTIME
// ===========================
useEffect(() => {

  const urlParamsLocal = new URLSearchParams(location.search);
  const tokenUrlLocal = urlParamsLocal.get("token");

  if (!tokenUrlLocal) {
    setTokenScaduto(true);
    setVerificaTokenLoading(false);
    return;
  }

  const tavoloRef = doc(
    db,
    "locali",
    normalizedLocaleTag,
    "tavoli",
    tableId
  );

  const unsub = onSnapshot(
    tavoloRef,
    (tavoloSnap) => {

      if (!tavoloSnap.exists()) {

        setTokenScaduto(true);
        setVerificaTokenLoading(false);
        return;
      }

      const { tokenAttuale } = tavoloSnap.data();

      // 🔥 TOKEN CAMBIATO → SESSIONE TERMINATA
      if (tokenUrlLocal !== tokenAttuale) {

        console.log(
          "❌ Token cambiato realtime → sessione chiusa"
        );

        setTokenScaduto(true);
        setVerificaTokenLoading(false);

        return;
      }

      setTokenScaduto(false);

      localStorage.setItem(
        "qr_token",
        tokenAttuale
      );

      setVerificaTokenLoading(false);
    },
    (e) => {

      console.error(
        "Errore listener token:",
        e
      );

      setTokenScaduto(true);
      setVerificaTokenLoading(false);
    }
  );

  return () => unsub();

}, [
  location.search,
  normalizedLocaleTag,
  tableId
]);

  // ===========================
// SESSIONE TAVOLO REALTIME / AI SALA
// ===========================
useEffect(() => {
  if (verificaTokenLoading) return;
  if (tokenScaduto) return;
  if (!tokenUrl) return;
  if (!normalizedLocaleTag) return;
  if (!tableId) return;
  if (!clienteId) return;

 const sessionId =
  `${normalizedLocaleTag}_${tableId}_${tokenUrl}`;

const attivazioneRef = doc(
  db,
  "attivazioniSessioni",
  sessionId
);

const scriviPing = async () => {
  try {

    const snap = await getDoc(attivazioneRef);

    if (!snap.exists()) {

      await setDoc(attivazioneRef, {
        locale: normalizedLocaleTag,
        tavolo: String(tableId),
        token: tokenUrl,
        clienteId,
        stato: "attivo",
        haOrdini: false,
        creatoIl: serverTimestamp(),
        ultimoPing: serverTimestamp()
      });

    } else {

      await updateDoc(attivazioneRef, {
        ultimoPing: serverTimestamp(),
        stato: "attivo"
      });

    }

  } catch (err) {
    console.error(
      "❌ Errore attivazione sessione:",
      err
    );
  }
};

  scriviPing();

  const intervalId = setInterval(() => {
    scriviPing();
  }, 30000);

  return () => {
    clearInterval(intervalId);
  };
}, [
  verificaTokenLoading,
  tokenScaduto,
  tokenUrl,
  normalizedLocaleTag,
  tableId,
  clienteId
]);

  // 📊 TRACKING SOLO PREVIEW / ADMIN
useEffect(() => {

  const hostname = window.location.hostname;

const isPreview =
  hostname === "qrmenu-italia.it";

  const isRealMenu =
    hostname.includes("menu-qrmenu.web.app") ||
    hostname.startsWith("menu.");

if (!isPreview && !isRealMenu)
  return;

  const trackMenuViewAccess =
    async () => {

      try {

        if (accessTrackedRef.current) return;
        if (verificaTokenLoading) return;
        if (tokenScaduto) return;
        if (!tokenUrl) return;
        // if (!clienteId) return;
        if (!normalizedLocaleTag) return;
        if (!tableId) return;

        // aspetta che il menu sia realmente inizializzato
        // if (loading) return;

        accessTrackedRef.current = true;

        await addDoc(
          collection(db, "analytics_menuview"),
          {
            localeTag:
              normalizedLocaleTag,

            tableId:
              String(tableId),

            token: tokenUrl,

            clienteId:
              clienteId || null,

            timestamp:
              serverTimestamp(),

            userAgent:
              navigator.userAgent || null,

            language:
              navigator.language || null,

            platform:
              navigator.platform || null,

            source: isRealMenu
              ? "menuview"
              : "preview_admin",

            type: "MenuView",
            orario: new Date().toISOString(),
            url: window.location.href,
            host: window.location.hostname,

          }
        );

        accessTrackedRef.current = true;

        console.log(
          "📊 Preview/Admin registrata"
        );

      } catch (err) {

        console.error(
          "❌ Errore tracking:",
          err
        );
      }
    };

  trackMenuViewAccess();

}, [
  verificaTokenLoading,
  tokenScaduto,
  tokenUrl,
  normalizedLocaleTag,
  tableId,
  clienteId,
  loading
]);

  const riVerificaToken = async () => {
    try {
      const urlParamsLocal = new URLSearchParams(location.search);
      const tokenUrlLocal = urlParamsLocal.get("token");
      if (!tokenUrlLocal) return false;

      const tavoloRef = doc(
        db,
        "locali",
        normalizedLocaleTag,
        "tavoli",
        tableId
      );

      const tavoloSnap = await getDoc(tavoloRef);
      if (!tavoloSnap.exists()) return false;

      const { tokenAttuale } = tavoloSnap.data();
      return tokenUrlLocal === tokenAttuale;
    } catch (e) {
      console.error("Errore ri-verifica token:", e);
      return false;
    }
  };

  // Fetch info locale
  useEffect(() => {
    const ref = doc(db, "locali", normalizedLocaleTag);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setInfoLocale(data);

          // 🔥 BLOCCO LOCALE
          if (data.stato === "bloccato") {
            setLocaleBloccato(true);
          } else {
            setLocaleBloccato(false);
          }
        }
      },
      (e) => console.error("Errore infoLocale:", e)
    );
    return () => unsub();
  }, [normalizedLocaleTag]);

  // Fetch menu + categorie

useEffect(() => {

  setLoading(true);

  const qMenu = query(
    collection(db, "menu"),
    where("localeTag", "==", normalizedLocaleTag)
  );

const qCategorie = query(
  collection(db, "categorie"),
  where("localeTag", "==", normalizedLocaleTag)
);
  const unsubMenu = onSnapshot(
    qMenu,
    async (snapMenu) => {

      try {

        const items = snapMenu.docs
          .map((d) => {

            const data = d.data();

            const nomeObj = {
              ...(typeof data.nome === "object"
                ? data.nome
                : { it: data.nome || "" })
            };

            const descObj = {
              ...(typeof data.descrizione === "object"
                ? data.descrizione
                : { it: data.descrizione || "" })
            };

            if (data.translations && typeof data.translations === "object") {
              Object.entries(data.translations).forEach(([lang, tr]) => {
                if (tr?.name) nomeObj[lang] = tr.name;
                if (tr?.description) descObj[lang] = tr.description;
              });
            }

            return {
              id: d.id,
              translations: data.translations || {},
              nome: nomeObj,
              descrizione: descObj,
              price: parseFloat(data.prezzo) || 0,
              image: data.immagineUrl || "",
              category: data.categoria || "Altro",
              allergeni: Array.isArray(data.allergeni)
                ? data.allergeni
                : (
                    typeof data.allergeni === "string"
                      ? data.allergeni
                          .split(",")
                          .map(a => a.trim())
                          .filter(Boolean)
                      : []
                  ),
              ingredienti: Array.isArray(data.ingredienti)
                ? data.ingredienti
                : (
                    typeof data.ingredienti === "string"
                      ? data.ingredienti
                          .split(",")
                          .map(i => i.trim())
                          .filter(Boolean)
                      : []
                  ),
              visibile: data.visibile !== false,
              inPromozione: data.inPromozione === true,
              ordine: data.ordine || 0
            };
          })
          .filter((x) => x.visibile);

        setDishes(items);

      } catch (e) {

        console.error("Errore menu realtime:", e);
        showSnack(TT.menuLoadError, "error");

      } finally {

        setLoading(false);

      }
    }

  );

  const unsubCategorie = onSnapshot(
    qCategorie,
    (snapCat) => {

      const cats = snapCat.docs
        .map((d) => ({
          id: d.id,
          ...d.data()
        }))
        .filter((c) => c.visibile !== false)
        .sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

      setCategorie(cats);
    },
    (e) => {
      console.error("Errore categorie realtime:", e);
    }
  );

  return () => {
    unsubMenu();
    unsubCategorie();
  };

}, [normalizedLocaleTag]);

  // Fetch traduzioni UI da Firestore + preferenze AI
  useEffect(() => {
    const fetchAIConfig = async () => {
      try {
        const uiTransRef = doc(db, "restaurants", normalizedLocaleTag, "ai", "uiTranslations");
        const uiSnap = await getDoc(uiTransRef);

        if (uiSnap.exists()) {
          setUiTranslations(uiSnap.data());
        }

        const prefsRef = doc(db, "ai_settings", normalizedLocaleTag);
        const prefsSnap = await getDoc(prefsRef);

        if (prefsSnap.exists()) {
          setAiPreferences(prefsSnap.data());
        } else {
          setAiPreferences(null);
        }
      } catch (e) {
        console.error("Errore caricamento configurazione AI:", e);
      }
    };

    fetchAIConfig();
  }, [normalizedLocaleTag]);

  // Fetch banner
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const snap = await getDocs(collection(db, "banners"));
        const attivi = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (b) =>
              b.localeTag?.toLowerCase() === normalizedLocaleTag.toLowerCase() &&
              b.attivo === true
          )
          .sort(
            (a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)
          );

        setBanners(attivi);
      } catch (e) {
        console.error("Errore banners:", e);
      }
    };
    fetchBanners();
  }, [normalizedLocaleTag]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const id = setInterval(
      () => setCurrentBannerIndex((i) => (i + 1) % banners.length),
      5000
    );
    return () => clearInterval(id);
  }, [banners.length]);

  // Fetch banner promozionali
  useEffect(() => {
    const fetchBannersPromozionali = async () => {
      try {
        const snap = await getDocs(collection(db, "banners_promozionali"));
        const attivi = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (b) =>
              b.localeTag?.toLowerCase() === normalizedLocaleTag.toLowerCase() &&
              b.attivo === true
          )
          .sort(
            (a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)
          );

        setBannersPromozionali(attivi);
      } catch (e) {
        console.error("Errore banners promo:", e);
      }
    };
    fetchBannersPromozionali();
  }, [normalizedLocaleTag]);

  useEffect(() => {
    if (bannersPromozionali.length <= 1) return;
    const id = setInterval(
      () =>
        setCurrentBannerPromoIndex(
          (i) => (i + 1) % bannersPromozionali.length
        ),
      5000
    );
    return () => clearInterval(id);
  }, [bannersPromozionali.length]);

  
  // Fetch sponsor popup
  useEffect(() => {
    const fetchSponsor = async () => {
      try {
        const snap = await getDocs(collection(db, "sponsor"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (s) =>
              s.localeTag?.toLowerCase() === normalizedLocaleTag.toLowerCase() &&
              s.attivo === true
          );

        setCurrentSponsor(null);
        setShowSponsor(false);

        if (list.length > 0) {
          setTimeout(() => {
            setCurrentSponsor(list[0]);
            setShowSponsor(true);
          }, 2000);
        }
      } catch (e) {
        console.error("Errore sponsor:", e);
      }
    };

    fetchSponsor();
  }, [normalizedLocaleTag, tableId]);

  // Fetch promozioni popup
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const snap = await getDocs(collection(db, "promozioni"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (p) =>
              p.localeTag?.toLowerCase() === normalizedLocaleTag.toLowerCase() &&
              p.attivo === true
          );

        setCurrentPromozione(null);
        setShowPromozione(false);

        if (list.length > 0) {
          setTimeout(() => {
            setCurrentPromozione(list[0]);
            setShowPromozione(true);
          }, 4000);
        }
      } catch (e) {
        console.error("Errore promozioni:", e);
      }
    };

    fetchPromos();
  }, [normalizedLocaleTag, tableId]);

  // Carrello live
  useEffect(() => {
    const ref = collection(db, ordinePath);
    const unsub = onSnapshot(ref, (snap) => {
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setOrdineCondiviso(lista);
    });
    return () => unsub();
  }, [ordinePath]);

  // ===========================
// LISTENER CHIAMATA CAMERIERE ATTIVA
// ===========================
useEffect(() => {

  if (!infoLocale.consentChiamataCameriere) return;

  const q = query(
    collection(db, "chiamateServizio"),
    where("chiaveTavolo", "==", `${normalizedLocaleTag}___${tableId}`),
    where("token", "==", tokenUrl),
    where("attiva", "==", true),
    where("gestita", "==", false)
  );

  const unsub = onSnapshot(q, (snap) => {
    setChiamataAttiva(!snap.empty);
  });

  return () => unsub();

}, [
  normalizedLocaleTag,
  tableId,
  tokenUrl,
  infoLocale.consentChiamataCameriere
]);

  // Fetch ordini inviati
  useEffect(() => {
    const qOrd = query(
      collection(db, "ordini"),
      where("locale", "==", normalizedLocaleTag),
      where("table", "==", tableId)
    );

    const unsub = onSnapshot(qOrd, (snap) => {
      const ord = snap.docs
        .map((d) => d.data())
        .filter((o) => o.stato !== "archiviato" && o.stato !== "pagamento");

      setOrdiniInviati(ord);
    });

    return () => unsub();
  }, [normalizedLocaleTag, tableId]);


  // Auto-scroll sidebar per mantenere categoria attiva visibile SOLO dopo interazione
  const userInteractedRef = useRef(false);

  // Segnala interazione utente su categorie
  useEffect(() => {
    const container = sidebarRef.current;
    if (!container) return;
    const handler = () => {
      userInteractedRef.current = true;
    };
    container.addEventListener('pointerdown', handler, { passive: true });
    return () => container.removeEventListener('pointerdown', handler);
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!sidebarRef.current || !activeCategory) return;

    // 🔥 BLOCCA solo primo render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const container = sidebarRef.current;
    const activeBtn = container.querySelector(
      `[data-cat="${activeCategory}"]`
    );

    if (!activeBtn) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();

    const offset =
      btnRect.left -
      containerRect.left -
      containerRect.width / 2 +
      btnRect.width / 2;

    container.scrollBy({
      left: offset,
      behavior: "smooth",
    });
  }, [activeCategory]);

  // IntersectionObserver per evidenziare categoria attiva durante lo scroll
  useEffect(() => {
    const titles = document.querySelectorAll(".mv-section-title");
    if (!titles.length) return;

    // Calcola l'altezza occupata da topbar + banner + categorie
    const topOffset = parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--topbar-height") || "72"
    ) + parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--banner-height") || "0"
    ) + parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--categories-height") || "86"
    );

    const vh = window.innerHeight;
    const topPct = Math.round((topOffset / vh) * 100);
    const bottomPct = 100 - topPct - 20; // lascia 20% di finestra visibile

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {

  // 🔥 se siamo quasi in top pagina → attiva "Tutto"
  const container = document.querySelector(".mv-main");

  if (container && container.scrollTop < 80) {
    setActiveCategory(null);
    return;
  }

  const id = visible[0].target.id;

  if (id?.startsWith("cat-")) {
    setActiveCategory(id.replace("cat-", ""));
  }
}
      },
      {
        root: null,
        rootMargin: `-${topPct}% 0px -${bottomPct}% 0px`,
        threshold: [0, 0.25, 0.5]
      }
    );

    titles.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [loading, categorie.length, bannersPromozionali.length]);

  // ===========================
  // INPUT HANDLERS
  // ===========================


  // ===========================
  // Risolve nome categoria con fallback
  // ===========================
  const resolveCategoryName = (cat, lingua) => {
    if (lingua === "it") return cat.nome || "";

    return (
      cat.translations?.[lingua]?.name ||
      cat.nome ||
      ""
    );
  };

  const handleInputChange = (dishId, field, value) => {
    setInputValues((prev) => ({
      ...prev,
      [dishId]: {
        ...(prev[dishId] || { quantity: 1, custom: "" }),
        [field]: value
      }
    }));
  };

  // Aggiungi al carrello
  const aggiungiItemTemp = async (dish) => {
    // 🔴 BLOCCO TOTALE AI
    if (infoLocale?.nascondiAI === true) {
      addDishToCart(dish, 1, "", new Set());
      return;
    }

    const tokenValido = await riVerificaToken();
    if (!tokenValido) {
      setTokenScaduto(true);
      showSnack(TT.sessionExpired, "error");
      return;
    }

    const values = inputValues[dish.id] || { quantity: 1, custom: "" };
    const custom = values.custom ? String(values.custom).trim() : "";
    const quantity = 1;

    const normalize = (s) => String(s || "").trim().toLowerCase();
    const regole = aiPreferences?.regole_suggerimenti || {};
    const regoleLocal = regole; // per compatibilità con il nuovo blocco
    const categoriaPiatto = normalize(dish.category || dish.categoria);
    let categorieSuggerite = Object.entries(regole)
      .find(([cat]) => normalize(cat) === categoriaPiatto)?.[1] || [];

    const intensita = aiPreferences?.intensita || "moderata";

    // 👉 SOFT: spesso NON mostra suggerimenti
    if (intensita === "soft") {
      if (Math.random() < 0.6) {
        addDishToCart(dish, quantity, custom, new Set());
        return;
      }
    }

    // 👉 AGGRESSIVA: se la categoria del piatto non ha regole configurate,
    // usa come fallback l'unione di TUTTE le categorie target definite
    // dal gestore in AIPreferenze (i valori delle regole), escludendo
    // la categoria del piatto corrente. Rispetta sempre AIPreferenze.
    if (intensita === "aggressiva") {
      if (!categorieSuggerite.length) {
        const tutteTargetCategorie = [
          ...new Set(
            Object.values(regoleLocal || {})
              .flat()
              .map(c => normalize(c))
              .filter(c => c && c !== categoriaPiatto)
          )
        ];
        if (tutteTargetCategorie.length > 0) {
          categorieSuggerite = tutteTargetCategorie;
        }
      }
    }

    // 👉 SICUREZZA FINALE: se ancora vuoto → NO popup
    if (!categorieSuggerite || categorieSuggerite.length === 0) {
      addDishToCart(dish, quantity, custom, new Set());
      return;
    }

    setSelectedSuggestions(new Set());
    setSuggestionPopup({
      open: true,
      dish,
      quantity,
      custom,
      suggestions: [],
      loading: false,
      asked: false
    });
  };

  // ===========================
  // HELPER ICONE CATEGORIA
  // ===========================
  const getCategoryIcon = (name = "") => {
    const n = String(name).toLowerCase();
    if (n.includes("pizza")) return "🍕";
    if (n.includes("primi")) return "🍝";
    if (n.includes("second")) return "🍖";
    if (n.includes("dolc")) return "🍰";
    if (n.includes("bev")) return "🥤";
    if (n.includes("anti")) return "🥗";
    if (n.includes("promo")) return "⭐";
    return "🍽️";
  };

// ===========================
// AVATAR DINAMICO CATEGORIA
// ===========================
const getCategoryAvatar = (catName) => {
  // 🚫 se menu no-foto → niente immagini categoria
  if (infoLocale.menuConFoto === false) return null;

  if (!dishes || dishes.length === 0) return null;

  const foundDish = dishes.find(
    (d) => d.category === catName && d.image
  );

  return foundDish ? foundDish.image : null;
};


  // ===========================
  // FUNZIONE SUGGERIMENTI INLINE
  // ===========================
  const getInlineSuggestions = (dish) => {
    // 🔴 DISABILITA SUGGERIMENTI DA ADMIN
    if (infoLocale?.nascondiAI === true) return [];
    if (infoLocale?.suggerimentiAI === false) return [];
    if (!aiPreferences || !dishes?.length) return [];

    const normalize = (s) => String(s || "").toLowerCase().trim();
    const getNome = (x) => typeof x.nome === "string" ? x.nome : (x.nome?.it || "");

    const categoriaPiatto = normalize(dish.category || dish.categoria);

    // Mai suggerire per bevande
    if (categoriaPiatto === "bevande") return [];

    const intensita = aiPreferences?.intensita || "moderata";
    const regole = aiPreferences?.regole_suggerimenti || {};
    const piattiSpinti = aiPreferences?.piatti_da_spingere || [];

    // Categorie suggerite dal gestore per questa categoria
    let categorieSuggerite = Object.entries(regole)
      .find(([cat]) => normalize(cat) === categoriaPiatto)?.[1]
      ?.map(c => normalize(c)) || [];

    // Fallback per intensità: soft = niente, moderata/aggressiva = tutte le target configurate
    if (!categorieSuggerite.length) {
      if (intensita === "soft") return [];
      // Aggressiva/moderata: unione di tutte le categorie target nelle regole
      const tutteTarget = [...new Set(
        Object.values(regole).flat().map(c => normalize(c))
      )].filter(c => c && c !== categoriaPiatto);

      if (tutteTarget.length) {
        categorieSuggerite = tutteTarget;
      } else {
        // Nessuna regola configurata: usa tutte le categorie diverse
        categorieSuggerite = [...new Set(
          dishes.map(d => normalize(d.category || d.categoria))
        )].filter(c => c !== categoriaPiatto && c !== "bevande");
      }
    }

    const candidati = dishes
      .filter(d => {
        if (d.id === dish.id) return false;
        const cat = normalize(d.category || d.categoria);
        const nome = normalize(getNome(d));
        if (!categorieSuggerite.includes(cat)) return false;
        if (cat === "bevande" && nome.includes("acqua")) return false;
        return true;
      })
      .map(d => ({
        ...d,
        // Score di abbinamento: usa solo ingredienti/gruppi, NON stessa categoria
        // (la stessa categoria è già esclusa dal filtro sopra)
        score: (() => {
          const s = calcolaSimilaritaPiattiAvanzata(dish, d);
          // Sottrae il bonus "stessa categoria" che non vogliamo qui
          const cat1 = normalize(dish.category || dish.categoria);
          const cat2 = normalize(d.category || d.categoria);
          return cat1 === cat2 ? Math.max(0, s - 0.2) : s;
        })()
      }));

    // Soglia adattiva: usa filtro solo se abbastanza candidati la superano
    const filtrati = candidati.filter(d => d.score > 0.1);
    const pool = filtrati.length >= 2 ? filtrati : candidati;

    // Seed deterministico basato sull'id del piatto: piatti diversi della stessa
    // categoria ottengono un offset diverso, così i suggerimenti variano per piatto.
    // Usiamo i char code dell'id per produrre un numero stabile ma diverso per ogni piatto.
    const dishSeed = String(dish.id || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);

    // Ordina: piatti spinti prima, poi per score, poi shuffle deterministico per parimerito
    const ordinati = [...pool].sort((a, b) => {
      const aSpinto = piattiSpinti.some(p => {
        if (!p) return false;
        const pn = normalize(getNome(p) || (typeof p === "string" ? p : ""));
        const an = normalize(getNome(a));
        return (p.id && a.id === p.id) || pn === an || an.includes(pn) || pn.includes(an);
      });
      const bSpinto = piattiSpinti.some(p => {
        if (!p) return false;
        const pn = normalize(getNome(p) || (typeof p === "string" ? p : ""));
        const bn = normalize(getNome(b));
        return (p.id && b.id === p.id) || pn === bn || bn.includes(pn) || pn.includes(bn);
      });
      if (bSpinto !== aSpinto) return bSpinto - aSpinto;
      // Score diversi: ordina per score
      const scoreDiff = (b.score || 0) - (a.score || 0);
      if (Math.abs(scoreDiff) > 0.05) return scoreDiff;
      // Score simili (parimerito): usa seed del piatto per variare la scelta
      // tra candidati equivalenti — piatto A mostra X, piatto B mostra Y
      const aCode = String(a.id || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const bCode = String(b.id || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
      return ((aCode + dishSeed) % 7) - ((bCode + dishSeed) % 7);
    });

    // Max 1 per categoria, max 2 totali
    const catViste = new Set();
    return ordinati.filter(d => {
      const cat = normalize(d.category || d.categoria);
      if (catViste.has(cat)) return false;
      catViste.add(cat);
      return true;
    }).slice(0, 2);
  };

  // ===========================
  // CARD COMPATTA - NUOVA STRUTTURA
  // Foto+Prezzo a sinistra | Info+Note/Pulsante a destra
  // ===========================

  const renderDishCard = (dish) => {

    const values = inputValues[dish.id] || { quantity: 1, custom: "" };
    const totalQtyForDish = getTotalQuantityForDish(dish.id);
    const { name, description, ingredients } = resolveDishText(dish, lingua);
    // menuConFoto: true di default se non impostato esplicitamente a false
    const conFoto = infoLocale.menuConFoto !== false;

    // Calcola badge promo/chef una sola volta
    const isSpinto = aiPreferences?.piatti_da_spingere?.some(p => {
      if (p.id && dish.id) return p.id === dish.id;
      const pn = String(typeof p.nome === "string" ? p.nome : p.nome?.it || p).toLowerCase().trim();
      const dn = String(typeof dish.nome === "string" ? dish.nome : dish.nome?.it).toLowerCase().trim();
      return pn === dn || dn.includes(pn) || pn.includes(dn);
    });
    const promoBadge = dish.inPromozione
      ? "🔥 Promo"
      : isSpinto
      ? "⭐ Chef"
      : null;

    return (
      <div
        key={dish.id}
        className={`dish-card${conFoto ? "" : " no-foto"}`}
        onClick={(e) => {
          if (e.target.closest("input") || e.target.closest("button")) return;
          setPopupDish(dish);
        }}
        style={{ cursor: "pointer" }}
      >
        {/* ── COLONNA SINISTRA: FOTO + badge promo SOTTO ── */}
        {conFoto && (
          <div className="dish-img-col">
            <div className="dish-image-wrapper">
              {dish.image ? (
                <img src={dish.image} alt={name} className="dish-image" />
              ) : (
                <div className="dish-no-image">🍽️</div>
              )}
              {totalQtyForDish > 0 && (
                <div className="dish-badge-overlay">{totalQtyForDish}</div>
              )}
            </div>
            {/* badge promo/chef SOTTO la foto */}
            {promoBadge && (
              <span className="promo-pill promo-pill--photo">{promoBadge}</span>
            )}
          </div>
        )}

        {/* ── COLONNA DESTRA: INFO ── */}
        <div className="dish-info-col">

          {/* RIGA 1: nome + badge quantità (no-foto) + prezzo */}
          <div className="dish-header-row">
            <span className="dish-name">{name}</span>
            {!conFoto && totalQtyForDish > 0 && (
              <span className="dish-badge-inline">{totalQtyForDish}</span>
            )}
            <span className="dish-price">{(dish.price || 0).toFixed(2)} €</span>
          </div>

          {/* RIGA 2: badge promo solo in no-foto */}
          {!conFoto && promoBadge && (
            <span className="promo-pill">{promoBadge}</span>
          )}

          {/* RIGA 3: descrizione */}
          {description && (
            <p className="dish-desc">{description}</p>
          )}

          {/* RIGA 4: suggerimenti inline */}
          {(() => {
            const suggerimenti = getInlineSuggestions(dish);
            if (!suggerimenti.length) return null;
            return (
              <div className="inline-suggestions">
                <span className="inline-suggestions-label">Abbina con:</span>
                <div className="inline-suggestions-list">
                  {suggerimenti.map((s) => {
                    const nome = typeof s.nome === "string" ? s.nome : s.nome?.it || "";
                    return (
                      <span key={s.id} className="inline-suggestion-pill">
                        {nome}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* RIGA 5: note + pulsante aggiungi */}
          {infoLocale.consentOrdine && (
            <div className="dish-action-row">
              <input
                type="text"
                className="note-input"
                placeholder={TT.notesHintPlaceholder || "Es: senza cipolla"}
                value={values.custom}
                onChange={(e) => handleInputChange(dish.id, "custom", e.target.value)}
              />
              <button
                type="button"
                className="add-btn"
                onClick={() => aggiungiItemTemp(dish)}
                aria-label="Aggiungi al carrello"
              >
                +
              </button>
            </div>
          )}

        </div>
      </div>
    );
  };

  // ===========================
  // Popup immagine premium
  // ===========================

  const PopupDish = () => {

    if (!popupDish) return null;

    const values = inputValues[popupDish.id] || {
      quantity: 1,
      custom: ""
    };

    const { name: resolvedName, description: resolvedDescription, ingredients } = resolveDishText(popupDish, lingua);
    const allergensForView = resolveAllergensForView(popupDish, lingua);

    return (
      <div
        className="popup-overlay"
        onClick={() => setPopupDish(null)}
      >
        <div
          className="popup-content"
          onClick={(e) => e.stopPropagation()}
        >
          {popupDish.image && (
            <img
              src={popupDish.image}
              alt={resolvedName}
              className="popup-image"
            />
          )}

          <div className="popup-info">
            <h2 className="popup-title">
              {resolvedName}
            </h2>


            {resolvedDescription && (

              <p className="popup-desc">
                {resolvedDescription}
              </p>
            )}

            {Array.isArray(ingredients) && ingredients.length > 0 && (
              <div className="popup-ingredients">
                <strong>Ingredienti:</strong>
                <p>{ingredients.join(", ")}</p>
              </div>
            )}

            {Array.isArray(allergensForView) && allergensForView.length > 0 && (
              <div className="popup-allergens">
                <strong>⚠️ {TT.allergens}:</strong>
                <div className="popup-allergen-list">
                  {allergensForView.map((a) => (
                    <span key={a} className="popup-allergen-badge">{a}</span>
                  ))}
                </div>
              </div>
            )}


            <div className="popup-price">
              {(popupDish.price || 0).toFixed(2)} €
              {popupDish.inPromozione && (
                <span className="promo-pill">🔥 Promo</span>
              )}
            </div>

            {infoLocale.consentOrdine && (
              <>
                <input
                  className="popup-note-input"
                  type="text"
                  placeholder={TT.notesHintPlaceholder || "Es: senza cipolla, ben cotto..."}
                  value={values.custom}
                  onChange={(e) =>
                    handleInputChange(
                      popupDish.id,
                      "custom",
                      e.target.value
                    )
                  }
                />

                <button
                  className="popup-add-btn"
                  onClick={() => {
                    aggiungiItemTemp(popupDish);
                    setPopupDish(null);
                  }}
                >
                  + {TT.add}
                </button>
              </>
            )}

            <button
              className="popup-close-btn"
              onClick={() => setPopupDish(null)}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===========================
  // CARICA SUGGERIMENTI SENZA AGGIUNGERE
  // ===========================
  const fetchAISuggestions = async (dish) => {
    if (infoLocale?.nascondiAI === true) return;
    if (suggestionPopup.loading) return;

    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions();
      const fn = httpsCallable(functions, "assistenteAbbinamentiAI");

      // 🔥 NUOVO: Calcola suggerimenti basati su similarità come fallback
      // 🔥 NORMALIZZAZIONE
      const normalizeLocal = (s) => String(s || "").trim().toLowerCase();

      const regoleLocal = aiPreferences?.regole_suggerimenti || {};
      const catBase = normalizeLocal(dish.category || dish.categoria);

      // 🔥 CATEGORIE CONSENTITE DAL GESTORE
      const categorieConsentite = (Object.entries(regoleLocal)
        .find(([cat]) => normalizeLocal(cat) === catBase)?.[1] || [])
        .map(c => normalizeLocal(c));

      // 🔥 FALLBACK CORRETTO (SOLO CATEGORIE GIUSTE, ORDINATO PER SIMILARITÀ AVANZATA)
      const fallbackSuggestions = dishes
        .filter(d => {
          if (d.id === dish.id) return false;

          const cat = normalizeLocal(d.category || d.categoria);
          const nome = normalizeLocal(typeof d.nome === "string" ? d.nome : d.nome?.it);

          if (!categorieConsentite.includes(cat)) return false;
          if (cat === catBase) return false;

          // 🔥 blocco bevande + acqua
          if (cat === "bevande") return false;
          if (cat === "bevande" && nome.includes("acqua")) return false;

          return true;
        })
        .map(d => ({
          ...d,
          score: calcolaSimilaritaPiattiAvanzata(dish, d)
        }))
        .filter(d => d.score > 0.15)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      // ✅ MOSTRA SUBITO QUESTO (NON SIMILARITÀ)
      setSuggestionPopup((prev) => ({
        ...prev,
        suggestions: [],
        loading: true
      }));

      // Logging debug opzionale
      if (debugMode) {
        console.log("=== DEBUG SUGGERIMENTI ===");
        console.log("Piatto base:", dish);
      }

      const res = await fn({
        localeTag: normalizedLocaleTag,
        menu: dishes,
        piattoBase: dish,
        preferenze: aiPreferences || {},
        // 🔥 NUOVO: Passa anche i suggerimenti calcolati localmente come hint
        hintSuggestions: fallbackSuggestions.map(s => ({
          id: s.id,
          nome: typeof s.nome === "string" ? s.nome : s.nome?.it || "",
          score: calcolaSimilaritaPiatti(dish, s)
        }))
      });

      const { suggerimenti, upsell, crossSell } = res.data || {};

      // Supporta sia il nuovo formato array che il vecchio upsell/crossSell
      let suggestionsFromAI = Array.isArray(suggerimenti) && suggerimenti.length
        ? suggerimenti
        : [upsell, crossSell].filter(Boolean);


      // (normalizeLocal, regoleLocal, catBase, categorieConsentite già definiti sopra)

      // 🔥 FILTRA AI
      let aiFiltrato = (suggestionsFromAI || [])
        .map(s => {
          const nome = normalizeLocal(s?.nome || "");
          if (!nome) return null;

          const match = dishes.find(d => {
            const n = normalizeLocal(
              typeof d.nome === "string" ? d.nome : d.nome?.it
            );
            return (
              n === nome ||
              n.startsWith(nome) ||
              nome.startsWith(n) ||
              n.includes(" " + nome) ||
              nome.includes(" " + n)
            );
          });

          return match ? { ...match, motivo: s.motivo } : null;
        })
        .filter(Boolean)
        .filter(d => {
          const cat = normalizeLocal(d.category || d.categoria);
          return categorieConsentite.includes(cat);
        });

      // BLOCCO ANTI-STESSA CATEGORIA (CRITICO)
      aiFiltrato = aiFiltrato
        .filter(d => {
          const cat = normalizeLocal(d.category || d.categoria);
          return cat !== catBase; // 🚫 mai stessa categoria
        });

      // 🔥 FILTRA fallback — shuffla per variare i suggerimenti ad ogni richiesta
      const fallbackFiltrato = fallbackSuggestions
        .filter(f => !aiFiltrato.some(a => a.id === f.id))
        .sort(() => Math.random() - 0.5)
        .slice(0, 2);

      // 🔥 UNIONE PULITA
      let aiSuggestions = aiFiltrato.length
        ? [...aiFiltrato, ...fallbackFiltrato]
        : fallbackSuggestions.slice(0, 3);

      // 🔥 PRIORITÀ PIATTI DA SPINGERE — porta in prima posizione se presenti
      const piattiSpinti = aiPreferences?.piatti_da_spingere || [];
      const getNomeS = (x) => typeof x.nome === "string" ? x.nome : (x.nome?.it || "");

      if (piattiSpinti.length > 0) {
        const spinto = aiSuggestions.find(s =>
          piattiSpinti.some(p => {
            if (p.id && s.id) return p.id === s.id;
            const pn = normalizeLocal(getNomeS(p) || (typeof p === "string" ? p : ""));
            const sn = normalizeLocal(getNomeS(s));
            return pn === sn || sn.includes(pn) || pn.includes(sn);
          })
        );
        if (spinto) {
          aiSuggestions = [spinto, ...aiSuggestions.filter(s => s.id !== spinto.id)];
        }
      }

      aiSuggestions = aiSuggestions.slice(0, 3);

      // Logging debug opzionale finale
      if (debugMode) {
        console.log("Suggerimenti finali:", aiSuggestions);
      }
      setSuggestionPopup((prev) => ({ 
        ...prev, 
        suggestions: aiSuggestions,
        loading: false 
      }));

      if (!aiSuggestions || aiSuggestions.length === 0) {
        showSnack(TT.noPairingFound || "Nessun abbinamento trovato", "info");
      }
    } catch (err) {
      console.error("Errore caricamento suggerimenti:", err);
      // In caso di errore AI, usa piatti delle categorie consentite in AIPreferenze
      const normalizeLocal = (s) => String(s || "").trim().toLowerCase();
      const regoleLocal = aiPreferences?.regole_suggerimenti || {};
      const catBase = normalizeLocal(dish.category || dish.categoria);
      const catConsentite = (Object.entries(regoleLocal)
        .find(([cat]) => normalizeLocal(cat) === catBase)?.[1] || [])
        .map(c => normalizeLocal(c));

      const fallbackDaCat = catConsentite.length
        ? dishes.filter(d => {
            if (d.id === dish.id) return false;
            const cat = normalizeLocal(d.category || d.categoria);
            return catConsentite.includes(cat);
          }).slice(0, 3)
        : [];

      if (fallbackDaCat.length > 0) {
        setSuggestionPopup((prev) => ({
          ...prev,
          suggestions: fallbackDaCat,
          loading: false
        }));
        showSnack(TT.pairingLoadError || "Usando suggerimenti locali", "info");
      } else {
        showSnack(TT.pairingLoadError || "Errore caricamento suggerimenti", "error");
        setSuggestionPopup((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  // ===========================
  // AGGIUNGI PIATTO E SUGGERIMENTI
  // ===========================
  const addDishToCart = async (dish, quantity, custom, selectedIds = new Set()) => {
    try {
      const resolvedName =
  typeof dish.nome === "string"
    ? dish.nome
    : dish.nome?.it || "";
      const ref = collection(db, ordinePath);
      const snap = await getDocs(ref);

      const esistente = snap.docs.find(
        (d) =>
          d.data().dishId === dish.id &&
          (d.data().custom || "") === custom
      );

      if (esistente) {
        const oldQty = esistente.data().quantity || 0;

        await updateDoc(esistente.ref, {
          quantity: oldQty + quantity,
          token: tokenUrl,
          timestamp: serverTimestamp()
        });
      } else {
        await addDoc(ref, {
          dishId: dish.id,
          name: resolvedName,
          price: dish.price,
          quantity,
          custom,
          clienteId,
          token: tokenUrl,
          timestamp: serverTimestamp()
        });
      }

      setInputValues((prev) => ({
        ...prev,
        [dish.id]: { quantity: 1, custom: "" }
      }));

      // Aggiungi i suggerimenti selezionati
      let addedCount = 1;
      if (selectedIds && selectedIds.size > 0 && suggestionPopup.suggestions) {
        const selectedSuggs = suggestionPopup.suggestions.filter((s) => selectedIds.has(s.id));
        
        for (const sugg of selectedSuggs) {
          if (sugg && sugg.id) {
            try {
              const ref2 = collection(db, ordinePath);
              const snap2 = await getDocs(ref2);

              const suggName =
  typeof sugg.nome === "string"
    ? sugg.nome
    : sugg.nome?.it || "";

              const esistente2 = snap2.docs.find(
                (d) => d.data().dishId === sugg.id && !d.data().custom
              );

              if (esistente2) {
  const oldQty2 = esistente2.data().quantity || 0;

  await updateDoc(esistente2.ref, {
    quantity: oldQty2 + 1,
    token: tokenUrl,
    timestamp: serverTimestamp()
  });
} else {
                await addDoc(ref2, {
                  dishId: sugg.id,
                  name: suggName,
                  price: sugg.price,
                  quantity: 1,
                  custom: "",
                  clienteId,
                  token: tokenUrl,
                  timestamp: serverTimestamp()
                });
              }
              addedCount++;
            } catch (e) {
              console.error("Errore aggiunta suggerimento:", e);
            }
          }
        }
      }

      if (addedCount > 1) {
        showSnack(`${addedCount} piatti aggiunti al carrello`, "success");
      } else {
        showSnack(TT.itemAdded, "success");
      }

      setSuggestionPopup({
        open: false,
        dish: null,
        suggestions: [],
        loading: false,
        quantity: 1,
        custom: ""
      });
      setSelectedSuggestions(new Set());
    } catch (e) {
      console.error("Errore aggiunta al carrello:", e);
      showSnack(TT.addError || "Errore aggiunta piatto", "error");

      setSuggestionPopup({
        open: false,
        dish: null,
        suggestions: [],
        loading: false,
        quantity: 1,
        custom: "",
        asked: false
      });
      setSelectedSuggestions(new Set());
    }
  };

  const eliminaItemTemp = async (item) => {
    try {
      const ref = doc(db, ordinePath, item.id);
      await deleteDoc(ref);
      showSnack(TT.removedFromOrder || "Piatto rimosso", "info");
    } catch (e) {
      console.error("Errore eliminaItemTemp:", e);
      showSnack("Errore durante la rimozione", "error");
    }
  };

  const chiamaServizio = async () => {
    if (chiamataAttiva) return;
    try {
      if (!infoLocale.consentChiamataCameriere) return;

      // Usa addDoc per tracciare ogni nuova chiamata
      await addDoc(collection(db, "chiamateServizio"), {
  locale: normalizedLocaleTag,
  tavolo: tableId,
  chiaveTavolo: `${normalizedLocaleTag}___${tableId}`,
  token: tokenUrl,
  timestamp: serverTimestamp(),
  stato: "nuova",
  attiva: true,
  gestita: false
});

      showSnack("🔔 Cameriere in arrivo", "success");
    } catch (e) {
      console.error("Errore chiamata cameriere:", e);
      showSnack(TT.callError, "error");
    }
  };

  const inviaOrdine = async () => {
    const tokenValido = await riVerificaToken();
    if (!tokenValido) {
      setTokenScaduto(true);
      showSnack(TT.sessionExpired, "error");
      return;
    }
  
    const ref = collection(db, ordinePath);
    const snap = await getDocs(ref);
    const itemsRaw = snap.docs.map((d) => d.data());

    const menuSnap = await getDocs(
      query(collection(db, "menu"), where("localeTag", "==", normalizedLocaleTag))
    );

    const menuMap = {};
    menuSnap.forEach((menuDoc) => {
      const data = menuDoc.data();
      menuMap[menuDoc.id] = data.categoria || "";
    });

    const items = itemsRaw.map((item) => ({
      ...item,
      categoria: menuMap[item.dishId] || ""
    }));
  
    if (items.length === 0) {
      showSnack(TT.noDishSelected, "error");
      return;
    }

    let totale = items.reduce((acc, it) => {
      return acc + ((it.price || 0) * (it.quantity || 0));
    }, 0);

    totale = Math.round(totale * 100) / 100;

    // Inizializza itemStati per ogni item
    const itemStatiInit = {};
    items.forEach((_, i) => {
      itemStatiInit[String(i)] = "inviato";
    });

    await addDoc(collection(db, "ordini"), {
      locale: normalizedLocaleTag,
      table: tableId,
      token: tokenUrl,
      chiaveTavolo: `${normalizedLocaleTag}___${tableId}`,
      items,
      note,
      stato: "inviato",
      itemStati: itemStatiInit, // 🔥 AGGIUNTO
      totale,
      timestamp: serverTimestamp(),

      // 🔥 AGGIUNGI QUESTO
      vistoInCucina: false
    });

    // 🔥 SESSIONE TAVOLO → ORA HA ORDINATO
try {

  const sessionId =
    `${normalizedLocaleTag}_${tableId}_${tokenUrl}`;

  await updateDoc(
    doc(db, "attivazioniSessioni", sessionId),
    {
      haOrdini: true,
      ultimoOrdine: serverTimestamp()
    }
  );

} catch (e) {

  console.error(
    "Errore update sessione tavolo:",
    e
  );

}
  
    try {
      await addDoc(collection(db, ordiniInviatiPath), {
        items: groupItemsByStato(items).map((it) => ({
          name: it.name,
          quantity: it.quantity,
          custom: it.custom || ""
        })),
        note,
        stato: "inviato",
        timestamp: serverTimestamp()
      });
    } catch (e) {
      console.error("Copia ordini inviati fallita:", e);
    }
  
    if (infoLocale.stampaAutomaticaOrdini) {
      const printContent = `
<div style="font-family: monospace; width: 300px; padding: 10px;">
  <h2 style="text-align:center;">${
    infoLocale.titolo || "COMANDA"
  }</h2>
  <strong>Tavolo: ${tableId}</strong><br/>
  <strong>Data: ${new Date().toLocaleString("it-IT")}</strong>
  <hr/>
  ${items
    .map(
      (it) =>
        `<div><strong>${it.quantity}x ${it.name}</strong>${
          it.custom ? "<br/><em>" + it.custom + "</em>" : ""
        }</div>`
    )
    .join("")}
  <hr/>
  Note: ${note || "Nessuna"}<br/>
  <h3 style="text-align:center;">Totale: €${totale.toFixed(2)}</h3>
</div>
`;
  
      const pw = window.open("", "", "width=400,height=600");
      pw.document.write(printContent);
      pw.document.close();
      pw.print();
    }
  
    for (const d of snap.docs) {
  try {
    await deleteDoc(d.ref);
  } catch (e) {
    console.error("Errore eliminazione item carrello:", e);
  }
}

setOrdineCondiviso([]);
setNote("");
setShowRiepilogo(false);

showSnack("🚀 Ordine inviato alla cucina", "success");
  };

  // ===========================
  // SIDEBAR RIEPILOGO - PULSANTI ORIZZONTALI
  // ===========================

  const SidebarRiepilogo = () => {
    if (!showRiepilogo) return null;

    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <h3>🧾 {TT.orderSummary}</h3>
          <button
            className="sidebar-close-btn"
            onClick={() => setShowRiepilogo(false)}
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        <div className="sidebar-content">
          {ordineCondiviso.length === 0 ? (
            <p className="empty-msg">{TT.noDishAdded}</p>
          ) : (
            ordineCondiviso.map((item) => (
              <div key={item.id} className="cart-item-row">
                <div className="cart-item-left">
                  <strong>{item.name}</strong>
                  {item.custom && (
                    <p className="cart-item-note">📝 {item.custom}</p>
                  )}
                </div>

                <div className="qty-controls">
                  <button
                    className="qty-btn"
                    onClick={async () => {

  if (item.quantity > 1) {

    try {

      await updateDoc(
        doc(db, ordinePath, item.id),
        {
          quantity: item.quantity - 1,
          timestamp: serverTimestamp()
        }
      );

    } catch (e) {

      console.error("Errore update quantità:", e);

    }

  } else {

    eliminaItemTemp(item);

  }
}}
                  >
                    −
                  </button>

                  <span className="qty-number">{item.quantity}</span>

                  <button
                    className="qty-btn"
                    onClick={async () => {

  try {

    await updateDoc(
      doc(db, ordinePath, item.id),
      {
        quantity: item.quantity + 1,
        timestamp: serverTimestamp()
      }
    );

  } catch (e) {

    console.error("Errore update quantità:", e);

  }

}}
                  >
                    +
                  </button>
                </div>
              </div>
            ))
          )}

          <textarea
            className="note-input"
            rows={2}
            placeholder={TT.notesPlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginTop: "10px" }}
          />
        </div>

{/* PULSANTI ORIZZONTALI IN BASSO */}
<div className="sidebar-actions">

  <button
    className="btn-sent"
    onClick={() => setShowRiepilogo(false)}
  >
    ✖ Chiudi
  </button>

  <button
    className="btn-sent"
    onClick={() => setShowOrdiniInviati(true)}
  >
    📋 {TT.sent}
  </button>

  <button
    className="btn-send"
    onClick={inviaOrdine}
  >
    🚀 {TT.send}
  </button>

</div>
      </div>
    );
  };

  // Sidebar Ordini Inviati
  const SidebarOrdiniInviati = () => {
    if (!showOrdiniInviati) return null;

    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <h3>📦 {TT.sentOrders}</h3>
          <button
            className="sidebar-close-btn"
            onClick={() => setShowOrdiniInviati(false)}
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        <div className="sidebar-content">
          {ordiniInviati.length === 0 ? (
            <p className="empty-msg">{TT.noOrderSent}</p>
          ) : (
            ordiniInviati.map((ord, idx) => (
              <div key={idx} className="sent-order-block">
                <strong>Stato: {ord.stato}</strong>
                <div className="sent-order-items">
                  {(() => {
                    const grouped = {};

                    (ord.items || []).forEach((it, i) => {
                      const statoItem =
                        ord.items?.[i]?.stato ||
                        ord.itemStati?.[String(i)] ||
                        ord.stato;

                      const key = `${it.name}_${it.custom || ""}_${statoItem}`;

                      if (!grouped[key]) {
                        grouped[key] = {
                          name: it.name,
                          custom: it.custom || "",
                          quantity: 0,
                          stato: statoItem
                        };
                      }

                      grouped[key].quantity += it.quantity;
                    });

                    return Object.values(grouped).map((it, i) => (
                      <p key={i}>
                        {it.quantity} × {it.name}
                        {it.custom ? ` (${it.custom})` : ""}
                        <span style={{
                          marginLeft: "8px",
                          fontSize: "12px",
                          opacity: 0.9,
                          fontWeight: 600,
                          color: getColor(it.stato),
                          border: `1.5px solid ${getColor(it.stato)}`,
                          borderRadius: "8px",
                          padding: "1px 8px",
                          background: "rgba(0,0,0,0.06)",
                          letterSpacing: "0.5px"
                        }}>
                          {it.stato}
                        </span>
                      </p>
                    ));
                  })()}
                </div>
                {ord.note && (
                  <p className="sent-note">📝 {ord.note}</p>
                )}
              </div>
            ))
          )}
        </div>

        <div className="sidebar-actions">
          <button
            className="btn-sent"
            onClick={() => setShowOrdiniInviati(false)}
          >
            ✖ Chiudi
          </button>
        </div>
      </div>
    );
  };

  // ===========================
  // AI post ordine, Sponsor, Promozioni
  // ===========================

  // ...existing code...

  const SponsorPopup = () => {
    if (!showSponsor || !currentSponsor) return null;

    return (
      <>
        <div
          className="popup-overlay"
          onClick={() => setShowSponsor(false)}
        />

        <div
          className="popup-card"
          style={{
            width:
              currentSponsor.dimensione === "fullscreen"
                ? "min(92vw, 500px)"
                : currentSponsor.dimensione === "grande"
                ? "min(92vw, 420px)"
                : currentSponsor.dimensione === "medio"
                ? "min(92vw, 380px)"
                : "min(92vw, 340px)"
          }}
        >
          {/* Header stile Instagram */}
          <div className="ig-post-header">
            <div className="ig-avatar">
              <span>{(currentSponsor.titolo || "S").charAt(0).toUpperCase()}</span>
            </div>
            <div className="ig-meta">
              <span className="ig-username">{currentSponsor.titolo || "Sponsor"}</span>
              <span className="ig-sponsored">Sponsorizzato</span>
            </div>
            <button
              className="popup-close-btn"
              onClick={() => setShowSponsor(false)}
            >
              ✕
            </button>
          </div>

          <div
            className="popup-img-wrapper"
            onClick={() => {
              if (currentSponsor.link) {
                window.open(
                  currentSponsor.link,
                  "_blank",
                  "noopener,noreferrer"
                );
                setShowSponsor(false);
              }
            }}
          >
            <img
              src={currentSponsor.imageUrl}
              alt={currentSponsor.titolo}
            />
          </div>

          {/* Footer stile Instagram */}
          <div className="ig-post-footer">
            <div className="ig-actions">
              <span className="ig-action-icon">♥</span>
              <span className="ig-action-icon">💬</span>
              <span className="ig-action-icon">✈</span>
            </div>
            {currentSponsor.titolo && (
              <p className="ig-caption">
                <strong>{currentSponsor.titolo}</strong>
              </p>
            )}
          </div>
        </div>
      </>
    );
  };

  const PromoPopup = () => {
    if (!showPromozione || !currentPromozione) return null;

    return (
      <>
        <div
          className="popup-overlay"
          onClick={() => setShowPromozione(false)}
        />

        <div
          className="popup-card"
          style={{
            width:
              currentPromozione.dimensione === "fullscreen"
                ? "min(92vw, 500px)"
                : currentPromozione.dimensione === "grande"
                ? "min(92vw, 420px)"
                : currentPromozione.dimensione === "medio"
                ? "min(92vw, 380px)"
                : "min(92vw, 340px)"
          }}
        >
          {/* Header stile Instagram */}
          <div className="ig-post-header">
            <div className="ig-avatar ig-avatar--promo">
              <span>{(currentPromozione.titolo || "P").charAt(0).toUpperCase()}</span>
            </div>
            <div className="ig-meta">
              <span className="ig-username">{currentPromozione.titolo || "Promozione"}</span>
              <span className="ig-sponsored">🔥 Offerta speciale</span>
            </div>
            <button
              className="popup-close-btn"
              onClick={() => setShowPromozione(false)}
            >
              ✕
            </button>
          </div>

          <div
            className="popup-img-wrapper"
            onClick={() => {
              if (currentPromozione.link) {
                window.open(
                  currentPromozione.link,
                  "_blank",
                  "noopener,noreferrer"
                );
                setShowPromozione(false);
              }
            }}
          >
            <img
              src={currentPromozione.imageUrl}
              alt={currentPromozione.titolo}
            />
          </div>

          {/* Footer stile Instagram */}
          <div className="ig-post-footer">
            <div className="ig-actions">
              <span className="ig-action-icon">♥</span>
              <span className="ig-action-icon">💬</span>
              <span className="ig-action-icon">✈</span>
            </div>
            {currentPromozione.titolo && (
              <p className="ig-caption">
                <strong>{currentPromozione.titolo}</strong>
              </p>
            )}
          </div>
        </div>
      </>
    );
  };

  // ===========================
  // RETURN COMPLETO
  // ===========================

  // 🔥 BLOCCO TOTALE LOCALE (PRIMA DI TUTTO)
  if (localeBloccato) {
    return (
      <div className="menu-root">
        <div className="token-screen expired">
          <div className="token-emoji">🚫</div>
          <h2>Locale non disponibile</h2>
          <p>
            Questo locale è momentaneamente disattivato.
            <br />
            <strong>Riprova più tardi.</strong>
          </p>
        </div>
      </div>
    );
  }

  if (verificaTokenLoading) {
    return (
      <div className="menu-root">
        <div className="token-screen">
          <div className="token-emoji">⏳</div>
          <h2>Verifica in corso...</h2>
        </div>
      </div>
    );
  }

if (tokenScaduto) {
  return (
    <div className="menu-root">
      <div className="session-ended-wrapper">

        <div className="session-ended-card">

          <div className="session-ended-icon">
            ⏳
          </div>

          <h1 className="session-ended-title">
            Sessione terminata
          </h1>

          <p className="session-ended-text">
            Questo tavolo non è più attivo oppure
            l’ordine è stato completato.
          </p>

          <div className="session-ended-divider" />

          <p className="session-ended-subtext">
            Scansiona nuovamente il QR code
            per iniziare una nuova sessione.
          </p>

          <button
            className="session-ended-button"
            onClick={() => window.location.reload()}
          >
            Aggiorna pagina
          </button>

        </div>

      </div>
    </div>
  );
}

  return (
  <div
    className={`menu-root ${
      bannersPromozionali.length > 0
        ? "has-banner"
        : ""
    }`}
  >
      {/* HEADER TOPBAR CON LOGO E LINGUA */}
      <div className="categoria-nav">
        {/* LOGO FISSO A SINISTRA */}
        {infoLocale.logoUrl && (
          <div className="nav-logo">
            <img
              src={infoLocale.logoUrl}
              alt={infoLocale.nome || "Logo"}
            />
          </div>
        )}

        {/* SELETTORE LINGUA DROPDOWN (Mobile-First) - PIÙ PICCOLO */}



        {(infoLocale.mostraSelezioneLingue || infoLocale.demo === true) &&
  availableLangs.length > 1 && (
    <div className="lang-dropdown-wrapper">
      <button
  type="button"
  className="lang-flag-button"
  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
  aria-label={`Lingua: ${lingua.toUpperCase()}`}
  aria-expanded={langDropdownOpen}
>
  <span>{langFlags[lingua] || "🌐"}</span>
  
</button>

      {langDropdownOpen && (
        <>
          <div
            className="lang-dropdown-overlay"
            onClick={() => setLangDropdownOpen(false)}
          />

          <div
            className="lang-dropdown"
            role="menu"
            aria-label="Seleziona lingua"
          >
            {availableLangs
              .filter((lang) => lang !== lingua)
              .map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="menuitem"
                  className="lang-dropdown-item"
                  aria-label={`Cambia lingua in ${lang.toUpperCase()}`}
                  onClick={() => {
                    setLingua(lang);
                    setLangDropdownOpen(false);
                  }}
                >
                  <span className="lang-dropdown-flag">
                    {langFlags[lang] || "🌐"}
                  </span>

                  <span className="lang-dropdown-label">
                    {langLabels[lang] || lang.toUpperCase()}
                  </span>
                </button>
              ))}
          </div>
        </>
      )}
    </div>
)}

{/* DEMO BACK BUTTON */}
{isDemo && (
  <button
    type="button"
    onClick={tornaAllaDemoDashboard}
    style={{
      background: "rgba(255,255,255,0.08)",
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.10)",
      borderRadius: "12px",
      padding: "0 14px",
      height: "42px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: "0.9rem",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      flexShrink: 0
    }}
  >
    ← Demo
  </button>
)}

        {/* PULSANTE AI NELLA NAVBAR */}
        <div style={{ 
          flex: 1, 
          minWidth: 0, 
          display: "flex", 
          justifyContent: "flex-end",
          gap: "8px"
        }}>
          {/* GIOCHI */}
<button
  type="button"
  aria-label="Apri giochi"
  title="Giochi"
  className={`mv-feature-button mv-feature-games ${
    showMenuIntro ? "mv-feature-button-highlight" : ""
  }`}
  style={{
              background: "rgba(255,255,255,0.10)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "12px",
              width: "42px",
              height: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)"
            }}
            onClick={() => {
              if (tokenUrl) {
                navigate(`/giochi/${localeTag}/${tableId}?token=${tokenUrl}`);
              } else {
                navigate(`/giochi/${localeTag}/${tableId}`);
              }
            }}
          >
<Icons.games
  size={20}
  aria-hidden="true"
/>

          </button>

 {/* QIRA */}
{!infoLocale.nascondiAI && (
  <button
  type="button"
  aria-label="Apri Qira"
  title="Qira"
  className={`mv-feature-button mv-feature-qira ${
    showMenuIntro ? "mv-feature-button-highlight" : ""
  }`}
  style={{
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#fff",
      fontWeight: "600",
      border: "none",
      borderRadius: "8px",
      padding: "8px 14px",
      fontSize: "14px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px"
    }}
    onClick={() => setAiOpen(true)}
  >
    <Icons.qira
      size={20}
      aria-hidden="true"
    />

    <span>Qira</span>
  </button>
)}
         </div>
      </div>

      {showMenuIntro && !infoLocale.nascondiAI && (
        <div
          className="mv-feature-intro"
          role="dialog"
          aria-modal="false"
          aria-label="Scopri Qira e i giochi"
        >
          <button
            type="button"
            className="mv-feature-intro-close"
            onClick={chiudiMenuIntro}
            aria-label="Chiudi presentazione"
          >
            ×
          </button>

          <div className="mv-feature-intro-title">
  Scopri le funzioni intelligenti del menu
</div>

          <div className="mv-feature-intro-item">
            <div className="mv-feature-intro-icon mv-feature-intro-icon-qira">
              <Icons.qira
                size={23}
                aria-hidden="true"
              />
            </div>

         <div>
  <strong>Qira, il tuo assistente intelligente</strong>

  <p>
    Qira utilizza l’Intelligenza Artificiale per aiutarti
    a scegliere i piatti in base a gusti, esigenze e budget.
  </p>

  <p className="mv-ai-transparency">
  I suggerimenti e gli abbinamenti del menu possono essere elaborati
  tramite Intelligenza Artificiale. Le risposte di Qira sono generate
  tramite Intelligenza Artificiale.
</p>
</div>
          </div>

          <div className="mv-feature-intro-item">
            <div className="mv-feature-intro-icon mv-feature-intro-icon-games">
              <Icons.games
                size={23}
                aria-hidden="true"
              />
            </div>

            <div>
              <strong>Giochi al tavolo</strong>

              <p>
                Divertiti mentre aspetti e scopri i giochi
                disponibili nel locale.
              </p>
            </div>
          </div>

          <div className="mv-feature-intro-actions">
            <button
              type="button"
              className="mv-feature-intro-qira-btn"
              onClick={() => {
                chiudiMenuIntro();
                setAiOpen(true);
              }}
            >
              <Icons.qira
                size={18}
                aria-hidden="true"
              />

              Prova Qira
            </button>

            <button
              type="button"
              className="mv-feature-intro-games-btn"
              onClick={() => {
                chiudiMenuIntro();

                if (tokenUrl) {
                  navigate(
                    `/giochi/${localeTag}/${tableId}?token=${tokenUrl}`
                  );
                } else {
                  navigate(`/giochi/${localeTag}/${tableId}`);
                }
              }}
            >
              <Icons.games
                size={18}
                aria-hidden="true"
              />

              Apri i giochi
            </button>
          </div>
        </div>
      )}

      <div className="top-spacer" />
{/* BANNER PROMOZIONALI */}
{bannersPromozionali.length > 0 && (
  <div
    className="mv-banner-wrapper"
    onClick={() => {
      const current =
        bannersPromozionali[currentBannerPromoIndex];

      if (current?.link) {
        window.open(
          current.link,
          "_blank",
          "noopener,noreferrer"
        );
      }
    }}
  >
    <img
      src={
        bannersPromozionali[
          currentBannerPromoIndex
        ]?.imageUrl
      }
      alt={
        bannersPromozionali[
          currentBannerPromoIndex
        ]?.titolo || "Banner"
      }
      className="mv-banner-image"
    />

    {bannersPromozionali[
      currentBannerPromoIndex
    ]?.titolo && (
      <div className="mv-banner-overlay">
        <span className="mv-banner-title">
          {
            bannersPromozionali[
              currentBannerPromoIndex
            ]?.titolo
          }
        </span>
      </div>
    )}
  </div>
)}

      {/* LAYOUT SHELL: CATEGORIA TOP-BAR + MAIN */}
      <div className="mv-shell">
        {/* BARRA CATEGORIE ORIZZONTALE — stile Glovo */}
        <aside className={`mv-sidebar ${infoLocale.menuConFoto === false ? "no-foto" : ""}`}>
          <div className="mv-sidebar-inner" ref={sidebarRef}>
            <button
              type="button"
              className={`mv-catbtn ${!activeCategory ? "active" : ""}`}
              onClick={() => {
                // setSelectedCategory(null); // eliminato
                setActiveCategory(null);

                const el = document.querySelector(".mv-main");
                if (el) el.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <span className="mv-caticon">🍽️</span>
              <span className="mv-catlabel">Tutto</span>
            </button>

            {categorie.map((cat) => (
              <button
                key={cat.id}
                type="button"
                data-cat={getCatId(cat.nome)}
                className={`mv-catbtn ${infoLocale.menuConFoto === false ? "no-foto" : ""} ${activeCategory === getCatId(cat.nome) ? "active" : ""}`}
                onClick={() => {
                  // setSelectedCategory(null); // eliminato
                  setActiveCategory(getCatId(cat.nome));

                  const el = document.getElementById(`cat-${getCatId(cat.nome)}`);
                  const container = document.querySelector(".mv-main");

                  if (el && container) {
                    const rect = el.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    const top = rect.top - containerRect.top + container.scrollTop - 20;

                    container.scrollTo({
                      top,
                      behavior: "smooth"
                    });
                  }
                }}
              >
                {infoLocale.menuConFoto !== false && (
                  <span className="mv-caticon">
                    {(() => {
                      const avatar = getCategoryAvatar(cat.nome);

                      if (avatar) {
                        return (
                          <img
                            src={avatar}
                            alt={resolveCategoryName(cat, lingua)}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "16px"
                            }}
                          />
                        );
                      }

                      return getCategoryIcon(resolveCategoryName(cat, lingua) || cat.nome);
                    })()}
                  </span>
                )}
                <span className="mv-catlabel">{resolveCategoryName(cat, lingua)}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* MAIN CONTENT AREA */}
        <main className="mv-main">
          {loading ? (
            <p
              style={{
                textAlign: "center",
                marginTop: "2rem"
              }}
            >
              ⏳ {TT.loading}
            </p>
          ) : (
            <div className="mv-content">
              {(() => {
                const categorieDaMostrare = categorie;
                return categorieDaMostrare.map((cat) => (
                  <section key={cat.id} className="mv-section">
                    <h3 id={`cat-${getCatId(cat.nome)}`} className="mv-section-title">
                      {resolveCategoryName(cat, lingua)}
                    </h3>

                    <div className="mv-grid">
                      {dishes
                        .filter((d) => d.category === cat.nome)
                        .sort((a, b) => (a.ordine || 0) - (b.ordine || 0))
                        .map((dish) => (
                          <div key={dish.id} className="mv-grid-item">
                            {renderDishCard(dish)}
                          </div>
                        ))}
                    </div>
                  </section>
                ));
              })()}
            </div>
          )}
        </main>
      </div>
      {/* GIOCHI FAB RIMOSSO - BOTTONE SPOSTATO NELLA NAVBAR */}
      {infoLocale.consentOrdine && !infoLocale.nascondiAI && (
        <>
{!showRiepilogo && !showOrdiniInviati && (
  <>
    {showChatCallout && !chatOpen && !showMenuIntro && (
      <div
        className="qira-menu-callout"
        onClick={apriChatQira}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            apriChatQira();
          }
        }}
      >
        <div className="qira-menu-callout-avatar">
          <Icons.qira
            size={20}
            aria-hidden="true"
          />
          <span className="qira-menu-callout-online" />
        </div>

        <div className="qira-menu-callout-content">
          <strong>Ciao! Sono Qira 👋</strong>
          <span>Hai bisogno di aiuto per scegliere?</span>
          <span>Chiedimi pure!</span>
        </div>

        <button
          type="button"
          className="qira-menu-callout-close"
          onClick={chiudiChatCallout}
          aria-label="Chiudi messaggio Qira"
        >
          ×
        </button>
      </div>
    )}

    <button
      type="button"
      className="chat-ai-fab"
      onClick={apriChatQira}
      aria-label="Apri Qira"
      title="Chatta con Qira"
    >
      <Icons.chat
        size={28}
        color="#ffffff"
        aria-hidden="true"
      />
    </button>
  </>
)}

          {/* CHAT AI CLIENTE */}
          {!infoLocale.nascondiAI && (
            chatOpen && (
              <ChatAICliente
                localeTag={normalizedLocaleTag}
                menu={dishes}
                open={chatOpen}
                onClose={() => setChatOpen(false)}
              />
            )
          )}
        </>
      )}

      {/* BOTTOM BAR */}
      <div className="bottom-bar">
        {infoLocale.consentChiamataCameriere && (
  <button
    className={`bottom-btn service ${
      chiamataAttiva ? "service-active" : ""
    }`}
    onClick={chiamaServizio}
  >
    {chiamataAttiva ? "🟢 Cameriere avvisato" : `🔔 ${TT.callService}`}
  </button>
)}

        {infoLocale.consentOrdine && (
          <button
            className="bottom-btn cart"
            onClick={() => setShowRiepilogo(true)}
          >
            🛒 {TT.orderSummary} ({totalePiatti})
            {totalePiatti > 0 && (
              <span className="bottom-cart-badge">
                {totalePiatti}
              </span>
            )}
          </button>
        )}
      </div>

      {/* SPACER */}
      <div className="bottom-spacer" />

      {/* POPUP IMMAGINE */}
      <PopupDish />

      {/* SIDEBAR RIEPILOGO */}
      <SidebarRiepilogo />

      {/* SIDEBAR ORDINI INVIATI */}
      <SidebarOrdiniInviati />

      {/* NUOVO AICliente – ai consigli dal menu reale */}
      {!infoLocale.nascondiAI && (
        <AICliente
          localeTag={normalizedLocaleTag}
          menu={dishes}
          lingua={lingua}
          openFromNavbar={aiOpen}
          onClose={() => setAiOpen(false)}
        />
      )}
      {/* POPUP SPONSOR */}
      <SponsorPopup />

      {/* POPUP PROMOZIONE */}
      <PromoPopup />

      {/* POPUP SUGGERIMENTI AI */}
      {suggestionPopup.open && !infoLocale.nascondiAI && (
        <SuggestionPopup
          suggestionPopup={suggestionPopup}
          selectedSuggestions={selectedSuggestions}
          setSuggestionPopup={setSuggestionPopup}
          setSelectedSuggestions={setSelectedSuggestions}
          fetchAISuggestions={fetchAISuggestions}
          addDishToCart={addDishToCart}
          TT={TT}
        />
      )}

      {/* SNACKBAR */}
      {snackbar.visible && (
        <div className="snackbar">{snackbar.message}</div>
      )}
    </div>
  );
}

export default MenuView;


